import { Head, Link, usePage } from '@inertiajs/react'
import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable'
import type { ImperativePanelHandle } from 'react-resizable-panels'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Toaster } from '@/components/ui/sonner'
import { UserAvatarDropdown } from '@/components/ui/user-avatar-dropdown'
import { useConversationChannel } from '@/hooks/useConversationChannel'
import { useProjectChannel } from '@/hooks/useProjectChannel'
import { useConversationState } from '@/hooks/useConversationState'
import { ChatPane } from '@/components/conversations/ChatPane'
import { PreviewPane } from '@/components/conversations/PreviewPane'
import { MobileConversationView } from '@/components/conversations/MobileConversationView'
import { Monitor, FileText, History, ChevronRight, Plug } from 'lucide-react'
import type { ConversationPageProps } from '@/types/conversations'

const STORAGE_KEY = 'cayuConversationPanelSizes'

export default function Show({
  conversation: initialConversation,
  messages: initialMessages,
  project,
  hasProcessingMessages: initialProcessing,
  groupedTodos: initialTodos,
  activeWorkflow: initialActiveWorkflow,
  organization
}: ConversationPageProps) {
  const { props } = usePage<any>()
  const flash = props.flash
  const { t } = useTranslation('conversations')

  // Panel size state with localStorage persistence
  const [panelSizes, setPanelSizes] = useState<number[]>([40, 60])
  const [isResizeHovered, setIsResizeHovered] = useState(false)
  const [isResizing, setIsResizing] = useState(false)
  const chatPanelRef = useRef<ImperativePanelHandle>(null)

  // Tab state for Preview/Product Spec
  const [activeTab, setActiveTab] = useState('preview')

  // Responsive tabs - show icon only when space is tight
  const [showTabText, setShowTabText] = useState(true)
  const headerRef = useRef<HTMLElement>(null)

  // Chat panel visibility state
  const [isChatHidden, setIsChatHidden] = useState(false)

  const toggleChatPane = () => {
    const panel = chatPanelRef.current
    if (panel) {
      if (panel.isCollapsed()) {
        panel.expand()
      } else {
        panel.collapse()
      }
    }
  }

  const handleChatPanelCollapse = (collapsed: boolean) => {
    setIsChatHidden(collapsed)
  }

  // Show toast notifications when flash messages change
  useEffect(() => {
    if (!flash) return

    if (flash.success) {
      toast.success(flash.success)
    }

    if (flash.notice) {
      toast.success(flash.notice)
    }

    if (flash.error || flash.alert) {
      const message = flash.error || flash.alert
      toast.error(message)
    }
  }, [flash])

  // Load saved panel sizes from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      try {
        const sizes = JSON.parse(saved)
        if (Array.isArray(sizes) && sizes.length === 2) {
          setPanelSizes(sizes)
        }
      } catch (e) {
        console.error('Failed to parse saved panel sizes:', e)
      }
    }
  }, [])

  // Monitor header width to show/hide tab text
  useEffect(() => {
    const header = headerRef.current
    if (!header) return

    const observer = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width
      // Show text when width is above 550px, hide when below (increased for 3 tabs)
      setShowTabText(width > 550)
    })

    observer.observe(header)
    return () => observer.disconnect()
  }, [])

  // Save panel sizes to localStorage when they change
  const handlePanelResize = (sizes: number[]) => {
    setPanelSizes(sizes)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sizes))
  }

  // State management with ActionCable integration
  const {
    conversation,
    messages,
    groupedTodos,
    processing,
    currentActivity,
    activityHistory,
    isSendingMessage,
    isAcceptingChanges,
    isDiscardingChanges,
    isInterrupting,
    project: currentProject,
    handleChannelEvent,
    handleProjectEvent,
    sendMessage,
    acceptChanges,
    discardChanges,
    approvePlan,
    submitAnswers,
    confirmButton,
    toggleMode,
    interruptProcessing,
    retryWorkflow,
    refetchData,
    nextStepSuggestions,
    collapsedMessageIds,
    expandedTurns,
    toggleTurnExpansion,
    startOptimisticWorkflow,
    activeWorkflow,
    isRetryingWorkflow,
    hasMoreMessages,
    isLoadingOlder,
    loadOlderMessages,
    memoryWarning,
    dismissMemoryWarning
  } = useConversationState({
    initialConversation,
    initialMessages,
    initialTodos,
    initialProcessing,
    initialActiveWorkflow: initialActiveWorkflow ?? null,
    projectId: project.id,
    initialProject: { id: project.id, name: project.name, mobile_included: project.mobile_included }
  })

  // Merge live updates from hook with full project props
  // currentProject contains partial updates (name, mobile_included) from ActionCable
  const displayProject = currentProject ? { ...project, ...currentProject } : project

  // Subscribe to ActionCable channel for real-time updates
  useConversationChannel({
    conversationId: conversation.id,
    onEvent: handleChannelEvent,
    onConnected: () => {
      console.log('✅ Real-time updates connected')

      // Check if we still have placeholder values and fetch fresh data
      // This catches broadcasts that fired before WebSocket connection was established
      const hasPlaceholderTitle = conversation.title === 'New Feature'
      const hasPlaceholderName = displayProject.name === 'Untitled Project'

      if (hasPlaceholderTitle || hasPlaceholderName) {
        // The broadcast likely fired BEFORE WebSocket connected (missed it)
        // Refetch to get the current values from DB
        // Force=true bypasses the processing guard — on first load, processing is always
        // true (first message is being handled) but we still need to fetch the real name
        console.log('🔄 Detected placeholder values, fetching fresh data...')
        refetchData(true)
      }
    },
    onDisconnected: () => {
      console.warn('❌ Real-time updates disconnected')
    }
  })

  const hasProjectChannelConnectedRef = useRef(false)
  useProjectChannel({
    projectId: project.id,
    onEvent: handleProjectEvent,
    onConnected: () => {
      if (hasProjectChannelConnectedRef.current) {
        refetchData()
      } else {
        hasProjectChannelConnectedRef.current = true
      }
    }
  })

  return (
    <>
      <Head title={conversation.title} />
      <Toaster richColors position="top-right" />
      {/* Mobile View - Tabbed Layout */}
      <div className="md:hidden h-screen w-full bg-background">
        <MobileConversationView
          conversation={conversation}
          project={displayProject}
          messages={messages}
          groupedTodos={groupedTodos}
          processing={processing}
          currentActivity={currentActivity}
          activityHistory={activityHistory}
          isSendingMessage={isSendingMessage}
          isAcceptingChanges={isAcceptingChanges}
          isDiscardingChanges={isDiscardingChanges}
          isInterrupting={isInterrupting}
          onSendMessage={sendMessage}
          onToggleMode={toggleMode}
          onInterrupt={interruptProcessing}
          onApprovePlan={approvePlan}
          onSubmitAnswers={submitAnswers}
          onConfirmButton={confirmButton}
          onAcceptChanges={acceptChanges}
          onDiscardChanges={discardChanges}
          nextStepSuggestions={nextStepSuggestions}
          collapsedMessageIds={collapsedMessageIds}
          expandedTurns={expandedTurns}
          onToggleTurnExpansion={toggleTurnExpansion}
          organization={organization}
          activeWorkflow={activeWorkflow}
          onAbortWorkflow={interruptProcessing}
          onRetryWorkflow={retryWorkflow}
          hasMoreMessages={hasMoreMessages}
          isLoadingOlder={isLoadingOlder}
          onLoadOlderMessages={loadOlderMessages}
        />
      </div>

      {/* Desktop View - Resizable Panels */}
      <div className="hidden md:flex h-screen w-full bg-background">
        <ResizablePanelGroup
          direction="horizontal"
          className="flex-1"
          onLayout={handlePanelResize}
        >
          {/* Chat Panel (Inset Sidebar Style) */}
          <ResizablePanel
            ref={chatPanelRef}
            defaultSize={panelSizes[0]}
            minSize={30}
            collapsible={true}
            collapsedSize={0}
            onCollapse={() => handleChatPanelCollapse(true)}
            onExpand={() => handleChatPanelCollapse(false)}
            className="overflow-hidden"
          >
            <div className="flex h-full flex-col items-center bg-content-bg min-w-0">
              <div className="flex h-full flex-col w-full max-w-3xl min-w-0">
                {/* Chat Header - Breadcrumb Navigation */}
                <header className="flex h-12 shrink-0 items-center px-4 lg:px-6 bg-content-bg">
                  <nav className="flex items-center gap-2 text-sm">
                    <Link
                      href={`/projects/${project.id}`}
                      className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors font-medium"
                    >
                      {displayProject.name}
                    </Link>
                    <ChevronRight className="h-4 w-4 text-muted-foreground/50 flex-shrink-0" />
                    <Link
                      href={`/projects/${project.id}/features`}
                      className="flex items-center gap-2 text-foreground hover:text-primary transition-colors font-semibold"
                    >
                      {conversation.title}
                    </Link>
                  </nav>
                </header>

                {/* Chat Content */}
                <div className="flex-1 overflow-hidden">
                  <ChatPane
                    conversation={conversation}
                    project={displayProject}
                    messages={messages}
                    groupedTodos={groupedTodos}
                    processing={processing}
                    currentActivity={currentActivity}
                    activityHistory={activityHistory}
                    isSendingMessage={isSendingMessage}
                    isAcceptingChanges={isAcceptingChanges}
                    isDiscardingChanges={isDiscardingChanges}
                    isInterrupting={isInterrupting}
                    onSendMessage={sendMessage}
                    onToggleMode={toggleMode}
                    onInterrupt={interruptProcessing}
                    onApprovePlan={approvePlan}
                    onSubmitAnswers={submitAnswers}
                    onConfirmButton={confirmButton}
                    nextStepSuggestions={nextStepSuggestions}
                    collapsedMessageIds={collapsedMessageIds}
                    expandedTurns={expandedTurns}
                    onToggleTurnExpansion={toggleTurnExpansion}
                    activeWorkflow={activeWorkflow}
                    memoryWarning={memoryWarning}
                    onDismissMemoryWarning={dismissMemoryWarning}
                  />
                </div>
              </div>
            </div>
          </ResizablePanel>

          <ResizableHandle
            className={`w-0 relative bg-transparent transition-all duration-200 cursor-col-resize mt-12 mb-2 ${isChatHidden ? 'pointer-events-none opacity-0' : ''}`}
            onMouseEnter={() => setIsResizeHovered(true)}
            onMouseLeave={() => setIsResizeHovered(false)}
            onDragging={(isDragging) => setIsResizing(isDragging)}
          />

          {/* Preview Panel (Main Content Area) */}
          <ResizablePanel
            defaultSize={panelSizes[1]}
            minSize={30}
            className="overflow-hidden"
          >
            <div className="flex h-full flex-col relative">
              {/* Header */}
              <header ref={headerRef} className="flex h-12 shrink-0 items-center gap-2 px-4 lg:px-6 bg-content-bg">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="h-9">
                    <TabsTrigger value="preview" className="gap-2" title={t('preview.title')}>
                      <Monitor className="h-4 w-4" />
                      {showTabText && <span>{t('preview.title')}</span>}
                    </TabsTrigger>
                    <TabsTrigger value="spec" className="gap-2" title={t('productSpec.title')}>
                      <FileText className="h-4 w-4" />
                      {showTabText && <span>{t('productSpec.title')}</span>}
                    </TabsTrigger>
                    <TabsTrigger value="history" className="gap-2" title={t('tabs.history')}>
                      <History className="h-4 w-4" />
                      {showTabText && <span>{t('tabs.history')}</span>}
                    </TabsTrigger>
                    <TabsTrigger value="integrations" className="gap-2" title={t('tabs.integrations')}>
                      <Plug className="h-4 w-4" />
                      {showTabText && <span>{t('tabs.integrations')}</span>}
                    </TabsTrigger>
                  </TabsList>
                </Tabs>

                <div className="ml-auto flex items-center gap-2">
                  <UserAvatarDropdown
                    projectId={project.id}
                    projectName={displayProject.name}
                    organization={organization}
                  />
                </div>
              </header>

              {/* Scrollable Content Area */}
              <div className="flex flex-1 flex-col overflow-hidden bg-content-bg pr-2 pb-2">
                <div className="flex flex-1 flex-col gap-4">
                  <div
                    className="h-full rounded-xl border bg-card text-foreground overflow-auto relative transition-all duration-200"
                    style={{
                      boxShadow: (isResizeHovered || isResizing)
                        ? '3px 4px 3px 1px rgba(10, 31, 63, 0.5)'
                        : undefined
                    }}
                  >
                    <PreviewPane
                      project={displayProject}
                      activeTab={activeTab}
                      isChatHidden={isChatHidden}
                      onToggleChat={toggleChatPane}
                      processing={processing}
                      groupedTodos={groupedTodos}
                      conversationMode={conversation.mode}
                      activeWorkflow={activeWorkflow}
                      onWorkflowStart={startOptimisticWorkflow}
                      onAbortWorkflow={interruptProcessing}
                      onRetryWorkflow={retryWorkflow}
                      isAbortingWorkflow={isInterrupting}
                      isRetryingWorkflow={isRetryingWorkflow}
                    />
                  </div>
                </div>
              </div>
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </>
  )
}
